import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gamesApi, blindsApi } from '../services/api';
import './DealerGameList.css';

function DealerGameList({ username, userId, userLocation, locationError, onSelectGame, onLogout }) {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [gameTimers, setGameTimers] = useState({}); // Track timer status for each game
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'

  const fetchGames = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = viewMode === 'my'
        ? await gamesApi.getDealerGames(userId)
        : await gamesApi.getAll();

      if (response.success) {
        const gamesList = response.data.games || response.data || [];
        setGames(gamesList);

        // Fetch timer status for each game
        const timerStatuses = {};
        await Promise.all(
          response.data.games.map(async (game) => {
            try {
              const blindResponse = await blindsApi.getBlindTimer(game.id);
              if (blindResponse.success && blindResponse.data.timer) {
                timerStatuses[game.id] = blindResponse.data.timer;
              }
            } catch (err) {
              console.log('Could not fetch timer for game:', game.id);
            }
          })
        );
        setGameTimers(timerStatuses);
      }
    } catch (err) {
      console.error('Error fetching dealer games:', err);
      setError(err.message || 'Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchGames();
  }, [userId, viewMode]);

  const handleSelectGame = (game) => {
    onSelectGame(game);
    navigate('/dealer/control');
  };

  const formatTime = (isoString, timeZone) => {
    const date = new Date(isoString);
    const options = { hour: 'numeric', minute: '2-digit', hour12: true };
    if (timeZone) options.timeZone = timeZone;
    return date.toLocaleTimeString('en-US', options);
  };

  return (
    <div className="dealer-game-list-container">
      <div className="dealer-header">
        <div className="header-top">
          <div className="header-left">
            <h1>Dealer Admin</h1>
            <p className="dealer-name">Welcome, {username}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="logout-button" onClick={() => navigate('/dealer/hub')}>Dealer Hub</button>
            <button className="logout-button" onClick={onLogout}>Logout</button>
          </div>
        </div>
      </div>

      <div className="games-section">
        <h2>Today's Games</h2>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'my' ? 'active' : ''}`}
            onClick={() => setViewMode('my')}
          >
            My Games
          </button>
          <button
            className={`toggle-btn ${viewMode === 'all' ? 'active' : ''}`}
            onClick={() => setViewMode('all')}
          >
            All Games
          </button>
        </div>
        <p className="games-date">
          {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>

        {loading && (
          <div className="loading-section">
            <div className="loading-spinner">♠</div>
            <p>Loading games...</p>
          </div>
        )}

        {error && (
          <div className="error-section">
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && games.length === 0 && (
          <div className="no-games-section">
            <p>No games assigned yet.</p>
          </div>
        )}

        {!loading && !error && games.length > 0 && (
          <div className="games-list">
            {games.map((game) => (
              <div
                key={game.id}
                className="game-card"
                onClick={() => handleSelectGame(game)}
              >
                <div className="game-card-header">
                  <h3>{game.venue?.name || 'Poker Game'}</h3>
                  <span className="game-time">
                    {new Date(game.startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: game.leagueTimezone || 'America/Denver' })}
                    <br />
                    Start Time: {formatTime(game.startTime, game.leagueTimezone)}
                  </span>
                </div>
                <p className="game-address">
                  <span className="location-icon">📍</span>
                  {game.venue?.address || ''}
                </p>
                <div className="game-stats">
                  <span className="player-count">
                    👥 {game.occupiedSeats}/{game.maxPlaces} Players
                  </span>
                  {gameTimers[game.id]?.timerStatus === 'running' || gameTimers[game.id]?.timerStatus === 'paused' ? (
                    <span className="game-status in-progress">
                      {gameTimers[game.id]?.timerStatus === 'paused' ? 'Paused' : 'In Progress'} - Rejoin
                    </span>
                  ) : (
                    <span className={`game-status ${game.isFull ? 'full' : ''}`}>
                      {game.isFull ? 'Full' : 'Ready to Start'}
                    </span>
                  )}
                </div>
                {game.waitingCount > 0 && (
                  <div className="waiting-info">
                    <span className="waiting-badge">
                      {game.waitingCount} on waiting list
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DealerGameList;
